/**
 * Assistente do CCO em gaveta lateral (e vínculo do Telegram e do WhatsApp, quando configurados) — Nexora Grid.
 *
 * Criado em: 17/10/2026 · Última modificação: 25/10/2026 · Versão: 2.3.0
 *
 * GUS-1501 (retorno de 18/10/2026: "o chat do CCO tem que ser em modal, hoje ele abre um bloco e bagunça o layout"):
 * botão flutuante em todas as telas do CCO e tecla A abrem uma gaveta sobreposta à direita; Esc fecha. A gaveta fica
 * montada fechada, então a conversa continua ao reabrir. Usa o chat comum (`ChatAssistente`), o mesmo do SCADA 3D.
 * O vínculo de cada canal só aparece com o canal configurado (bot do Telegram, número do WhatsApp — US-301).
 * US-3101: o chat só responde com o celular cadastrado (passkey). Sem aparelho, o topo mostra "cadastrar celular", que
 * gera o link de uso único para abrir no próprio celular.
 * US-3104: "revogar" ao lado do 🔐 (celular perdido) — o chat para até cadastrar outro.
 * US-3201: lista de conversas com busca (`comConversas`) e "Nova conversa".
 */
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, ErroApi } from "../api";
import { ChatAssistente } from "../ChatAssistente";
import { useCco } from "./contexto";

type Canal = "telegram" | "whatsapp";
type Vinculos = { vinculos: { canal: string; conta: string; empresa: string; desde: string; liberado?: boolean; aparelho?: string | null;
  aparelho_ate?: string | null }[]; telegram_bot: string; whatsapp_numero?: string };
type Codigo = { canal: Canal; codigo?: string; instrucao?: string; expira?: string; erro?: string };

const SUGESTOES = ["como estão os reservatórios?", "quais bombas estão ligadas?", "tem alarme crítico?", "por que o nível do RAP05 está assim?"];
const NOME: Record<Canal, string> = { telegram: "Telegram", whatsapp: "WhatsApp" };

export function Assistente() {
  const { empresa } = useCco();
  const local = useLocation();
  const [aberta, setAberta] = useState(false);
  const [codigo, setCodigo] = useState<Codigo | null>(null);
  const [vinculos, setVinculos] = useState<Vinculos | null>(null);
  const [linkAparelho, setLinkAparelho] = useState<{ url?: string; expira?: string; erro?: string } | null>(null);
  const gaveta = useRef<HTMLElement>(null);
  const botao = useRef<HTMLButtonElement>(null);

  const carregarVinculos = () => api<Vinculos>("/v1/assistente/canais").then(setVinculos).catch(() => undefined);
  useEffect(() => { void carregarVinculos(); }, []);
  // links antigos (#assistente) e o menu abrem a gaveta
  useEffect(() => { if (local.hash === "#assistente") setAberta(true); }, [local.hash]);
  useEffect(() => {
    if (aberta) window.setTimeout(() => gaveta.current?.querySelector<HTMLInputElement>("input")?.focus(), 50);
  }, [aberta]);
  useEffect(() => {
    const tecla = (ev: KeyboardEvent) => {
      const digitando = (ev.target as HTMLElement)?.matches?.("input, textarea, select, [contenteditable]");
      if (ev.key === "Escape" && aberta) { setAberta(false); botao.current?.focus(); return; }
      if (!digitando && !ev.ctrlKey && !ev.metaKey && !ev.altKey && ev.key.toLowerCase() === "a") { ev.preventDefault(); setAberta(true); }
    };
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, [aberta]);

  const gerarCodigo = async (canal: Canal) => {
    try {
      const r = await api<{ codigo: string; instrucao: string; expira_em: string }>(`/v1/assistente/canais/${canal}/codigo`, { corpo: { empresa } });
      setCodigo({ canal, codigo: r.codigo, instrucao: r.instrucao, expira: new Date(r.expira_em).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) });
    } catch (e) {
      setCodigo({ canal, erro: e instanceof ErroApi ? e.message : "falha de rede" });
    }
  };
  const desvincular = async (canal: Canal) => {
    await api(`/v1/assistente/canais/${canal}`, { metodo: "DELETE" }).catch(() => undefined);
    void carregarVinculos();
  };
  const revogarCelular = async (canal: Canal) => {
    const meus = await api<{ id: string; canal: string; estado: string }[]>("/v1/aparelhos/meus").catch(() => []);
    const ativo = meus.find((a) => a.canal === canal && a.estado === "ativo");
    if (!ativo || !window.confirm("Revogar este celular? O chat para de responder até você cadastrar outro.")) return;
    await api(`/v1/aparelhos/${ativo.id}/revogar`, { corpo: { motivo: "revogado pelo próprio usuário no CCO" } }).catch(() => undefined);
    void carregarVinculos();
  };
  const gerarLinkAparelho = async (canal: Canal) => {
    try {
      const r = await api<{ url: string; expira_em: string }>(`/v1/assistente/canais/${canal}/aparelho`, { corpo: {} });
      setLinkAparelho({ url: r.url, expira: new Date(r.expira_em).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) });
    } catch (e) {
      setLinkAparelho({ erro: e instanceof ErroApi ? e.message : "falha de rede" });
    }
  };
  const configurado: Record<Canal, boolean> = { telegram: Boolean(vinculos?.telegram_bot), whatsapp: Boolean(vinculos?.whatsapp_numero) };
  const topo = (["telegram", "whatsapp"] as Canal[]).map((canal) => {
    const vinculado = vinculos?.vinculos.find((v) => v.canal === canal);
    if (vinculado) {
      return <span key={canal} className="nx-suave">{NOME[canal]}: {vinculado.conta || "vinculado"}{" "}
        {vinculado.aparelho ? <small>· 🔐 {vinculado.aparelho}{" "}
          <button type="button" className="nx-botao" onClick={() => void revogarCelular(canal)}>revogar</button> </small>
          : <button type="button" className="nx-botao" title={vinculado.liberado ? "prazo para cadastrar o celular" : "o chat está parado até cadastrar o celular"}
            onClick={() => void gerarLinkAparelho(canal)}>cadastrar celular{vinculado.liberado ? "" : " (parado)"}</button>}{" "}
        <button type="button" className="nx-botao" onClick={() => desvincular(canal)}>desvincular</button></span>;
    }
    if (!configurado[canal]) return null;
    return <button key={canal} type="button" className="nx-botao"
      onClick={() => (codigo?.canal === canal ? setCodigo(null) : void gerarCodigo(canal))}>Vincular {NOME[canal]}</button>;
  }).filter(Boolean);
  const jaVinculado = codigo ? vinculos?.vinculos.some((v) => v.canal === codigo.canal) : false;

  return (
    <>
      <button ref={botao} type="button" className="abrir-assistente" aria-controls="gaveta-assistente" aria-expanded={aberta}
        onClick={() => setAberta(!aberta)}>◈ Assistente <kbd>A</kbd></button>
      <aside ref={gaveta} id="gaveta-assistente" className={`gaveta-assistente${aberta ? " aberta" : ""}`} aria-label="Assistente do CCO"
        aria-hidden={!aberta} inert={!aberta ? true : undefined}>
        <div className="gaveta-topo">
          <span className="nx-suave">perguntas padrão direto da telemetria · análise pelo agente de IA · nunca manobra</span>
          <button type="button" className="nx-botao" onClick={() => { setAberta(false); botao.current?.focus(); }}>Fechar (Esc)</button>
        </div>
        <ChatAssistente empresa={empresa} titulo="Assistente do CCO" rotuloEntrada="Pergunta ao assistente" sugestoes={SUGESTOES} comConversas
          topoExtra={topo.length ? <>{topo}</> : null} />
        {linkAparelho && (
          <div className="vinculo-telegram" role="status">
            {linkAparelho.erro ? <span>Não foi possível gerar o link: {linkAparelho.erro}</span> : (
              <>
                <span>Abra este link <b>no celular vinculado</b> e confirme com a biometria ou o PIN (vale até {linkAparelho.expira}, uso único):</span>
                <code className="link-aparelho">{linkAparelho.url}</code>
                <span className="nx-suave">O mesmo link chega no chat quando você manda uma mensagem ao bot.</span>
              </>
            )}
            <button type="button" className="nx-botao" onClick={() => { setLinkAparelho(null); void carregarVinculos(); }}>fechar</button>
          </div>
        )}
        {codigo && !jaVinculado && (
          <div className="vinculo-telegram" role="status">
            {codigo.erro ? <span>Não foi possível gerar o código: {codigo.erro}</span> : (
              <>
                <span>Código de vínculo do {NOME[codigo.canal]}: <b className="nx-valor codigo">{codigo.codigo}</b> (vale até {codigo.expira}, uso único)</span>
                <span className="nx-suave">{codigo.instrucao}</span>
                <button type="button" className="nx-botao" onClick={() => { setCodigo(null); void carregarVinculos(); }}>já enviei</button>
              </>
            )}
          </div>
        )}
      </aside>
    </>
  );
}
